// eslint-disable-next-line max-classes-per-file

import FileMixin from '@mixins/file-mixin';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import { buildWebStorage, setupCache } from 'axios-cache-interceptor';
import { wrapper } from 'axios-cookiejar-support';
import Cookies from 'js-cookie';
import swal from 'sweetalert2';
import { CookieJar } from 'tough-cookie';

import ApiError from '@/exceptions/ApiError';
import ApiInternalError from '@/exceptions/ApiInternalError';
import ApiValidationError from '@/exceptions/ApiValidationError';

const jar = new CookieJar();
const instance = wrapper(axios.create({
    jar,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        withCredentials: true,
        'X-Frontend-Version': COMMIT_HASH,
    },
}));

const ApiClient = setupCache(instance, {
    storage: buildWebStorage(localStorage, 'xlinx-http-cache:'),
    exclude: {
        query: ['filter[search]'],
        paths: [
            '/version.json',
        ],
    },
});

ApiClient.getCollection = async (url, map, queryParams) => {
    const params = { };
    if (queryParams) {
        params.params = queryParams;
    }

    const response = await ApiClient.get(url, params);

    let collection = response.data.data;

    if (typeof map === 'function') {
        collection = collection.map((c) => (map(c)));
    }

    return collection;
};

const abortControllers = {};

function abortRequest(id) {
    if (abortControllers[id]) {
        abortControllers[id].abort();
        delete abortControllers[id];
    }
}

function createAbortController(id) {
    const controller = new AbortController();
    abortControllers[id] = controller;
    return controller;
}

let abortController;

ApiClient.search = async (id, url, map, params) => {
    if (abortController) abortRequest(id);
    abortController = createAbortController(id);
    const response = await ApiClient.get(url, { params, signal: abortController.signal });
    abortController = undefined;
    return response.data;
};

ApiClient.getItem = async (url, params) => {
    const response = await ApiClient.get(url, { params });

    return response.data;
};

ApiClient.openFile = async (url, params, downloadFile = false) => {
    const response = await ApiClient.get(url, { params });

    FileMixin.methods.showFile(response.data.content, response.data.friendly_name || response.data.title, response.data.mime_type, downloadFile);
};

export { ApiError, ApiValidationError };
ApiClient.interceptors.request.use(
    async (config) => {
        const xsrfToken = Cookies.get('XSRF-TOKEN');
        if (!xsrfToken) {
            // Refresh our session token
            await axios.get('/api/sanctum/csrf-cookie');
        }

        // Route to testing database when running inside Cypress
        // eslint-disable-next-line no-param-reassign
        if (window.Cypress) config.headers = { ...config.headers, 'X-Cypress': 'true' };

        return config;
    },
    (error) => Promise.reject(error),
);

// Add a response interceptor
ApiClient.interceptors.response.use((response) => response, async (error) => {
    // This is normally because of a code error

    if (!error.response) {
        return Promise.reject(error);
    }

    if (error.response?.status === 500) {
        return Promise.reject(new ApiInternalError(error.response.data.error, error.response.status));
    }

    if (error.response.status === 419) {
        // Refresh our session token
        await axios.get('/api/sanctum/csrf-token');

        // Return a new request using the original request's configuration
        return axios(error.response.config);
    }

    if (error.response.status === 401 && error.response.config.url !== '/api/user/me') {
        if (!swal.isVisible()) {
            swal.fire({ icon: 'error', title: 'Unauthorized access', html: 'please refresh the page and login again.' });
        }
        return Promise.reject(error);
    }

    if (error.response.status === 422) {
        return Promise.reject(new ApiValidationError(error.response.data.message, error.response.data.errors, 422));
    }

    if (error.response.data.error?.match(/Requested filter\(s\) (.*) are not allowed/)) {
        localStorage.clear();
    }

    Sentry.setContext('Api', error);
    return Promise.reject(new ApiError(error.response.data.error, error.response.status));
});

export default ApiClient;
